import { useState } from "react";

import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack
} from "@chakra-ui/react";

import { EMAIL_FORWARDING_SENDER_REASON_OPTIONS } from "@/util/constants";

export type ReasonOption = {
  value: string;
  label: string;
};

interface SenderConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description: string;
  senderEmail?: string;
  isLoading?: boolean;
  confirmText?: string;
  loadingText?: string;
  defaultReason?: string;
  onConfirm: (reason: string) => void;
  senderType?: "ALLOWED" | "REJECTED" | "BLOCKED";
}

export const ConfirmSender: React.FC<SenderConfirmModalProps> = ({
  isOpen,
  onClose,
  title = "Revocation Confirmation",
  description,
  senderEmail,
  isLoading,
  confirmText = "Confirm",
  loadingText,
  defaultReason = "",
  onConfirm,
  senderType = "REJECTED"
}) => {
  const [selectedReason, setSelectedReason] = useState(defaultReason);
  const [otherReason, setOtherReason] = useState("");

  const handleClose = () => {
    setSelectedReason(defaultReason);
    setOtherReason("");
    onClose();
  };

  const handleConfirm = () => {
    const finalReason =
      selectedReason === "Other" ? otherReason : selectedReason;
    onConfirm(finalReason);
    handleClose();
  };

  const showReasonField = senderType === "REJECTED" || senderType === "BLOCKED";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          fontSize="xl"
          fontWeight="600"
          borderBottom="1px solid #E0E0E0"
        >
          {title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4} width="100%">
            <Text>{description.replace("[email]", senderEmail || "")}</Text>
            {showReasonField &&
              EMAIL_FORWARDING_SENDER_REASON_OPTIONS.length > 0 && (
                <Box>
                  <Text mb={2} fontWeight={500}>
                    Reason
                  </Text>
                  <Select
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    mb={selectedReason === "Other" ? 2 : 0}
                  >
                    {EMAIL_FORWARDING_SENDER_REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {selectedReason === "Other" && (
                    <Input
                      placeholder="Type reason here..."
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                    />
                  )}
                </Box>
              )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isLoading}
            size="lg"
            border="1px solid #E0E0E0"
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleConfirm}
            isLoading={isLoading}
            loadingText={loadingText}
            size="lg"
            isDisabled={
              showReasonField &&
              selectedReason === "Other" &&
              !otherReason.trim()
            }
          >
            {confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
